import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SalesInvoice, SALES_INVOICE_STATUS_COLORS } from '../../interfaces/sales/salesInvoice.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { SALES_INVOICE_CREATE_ROLES } from '../../constants/salesRoles';

const SalesInvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const userRoles: string[] = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
  // The POST endpoint 403s every other role (e.g. InstitutionManager, who can still read the list).
  const canCreate = SALES_INVOICE_CREATE_ROLES.some(role => userRoles.includes(role));
  const [rows, setRows] = useState<SalesInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await SalesInvoiceService.listByInstitution(institutionId, page, size);
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Sales Invoices</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/invoices/new')}>
            New Invoice
          </Button>
        )}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Overdue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/invoices/${row.id}`)}
                >
                  <TableCell>{row.invoiceNumber ?? 'Draft'}</TableCell>
                  <TableCell>{row.customerName ?? '—'}</TableCell>
                  <TableCell>{row.issueDate}</TableCell>
                  <TableCell>{row.dueDate}</TableCell>
                  <TableCell>{row.totalAmount}</TableCell>
                  <TableCell>{row.paidAmount}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      color={SALES_INVOICE_STATUS_COLORS[row.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {row.overdue === true && (
                      <Chip label="Overdue" color="error" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default SalesInvoiceList;
